<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import axios from 'axios';
import { useUserForm } from 'src/composables/useUserForm';
import { useAnalytics } from 'src/composables/analytics';
import Modal from 'components/Modal.vue';

const props = defineProps({
  pigment: {
    type: Object,
    required: true,
  },
  selectedIndustry: {
    type: String,
    required: false,
    default: null,
  }
});

const store = useStore();

const {
  generic: { services }, fieldManifest, user: { activeUser }
} = store.state;

const finished = ref(false);

const regulatoryDocumentTypes = computed(() => (
  Object.values(fieldManifest).filter((field) => field.dataType === 'document' && field.documentCategory === 'regulatory')
));

const documentTypeOptions = computed(() => (
  Object.fromEntries(
    regulatoryDocumentTypes.value.map((field) => [field.code, field.title])
  )
));

const colorImage = computed(() => {
  if (props.pigment.stackPreviewImage?.url) {
    return props.pigment.stackPreviewImage.url;
  }

  // handle actual image (image_color_chip asset)
  const colorChipAssets = props.pigment.assets
    && Array.isArray(props.pigment.assets)
    && props.pigment.assets.find((asset) => asset.type === 'image_color_chip');
  if (colorChipAssets) {
    return `${services.documentservice.url}/api/v2/document/download/${colorChipAssets.id}`;
  }

  if (props.pigment.color_values
    && typeof props.pigment.color_values === 'object'
    && props.pigment.color_values.hasOwnProperty('image')
    && props.pigment.color_values.image) {
    return props.pigment.color_values.image;
  }

  return null;
});

const { handleSubmit, resetForm } = useForm();

const {
  isSending,
  formSecurityToken,
  consentHintNecessary,
  links,
  message,
  hasMessage,
} = useUserForm();

const { trackEvent } = useAnalytics();

const submit = handleSubmit(async (values) => {
  isSending.value = true;

  const data = {
    formSecurityToken: formSecurityToken.value,
    ...values,
    productId: props.pigment.id,
    productName: props.pigment.product_name,
    firstName: activeUser.firstName,
    lastName: activeUser.lastName,
    email: activeUser.email,
    company: activeUser.company,
    selectedIndustry: props.selectedIndustry,
  };

  try {
    await axios.post(`${location.origin}/api/v1/message/requestRegulatoryDocument`, data);

    trackEvent(
      'Form',
      'request_regulatory_document',
      {
        prd: props.pigment.prd,
        productName: props.pigment.product_name,
      }
    );

    finished.value = true;
  } catch (e) {
    message.value = {
      type: 'error',
      content: e.response?.data?.message ?? e.message,
    };
  }

  isSending.value = false;
});

const modalVisible = ref(false);

function showModal() {
  modalVisible.value = true;
}

function hideModal() {
  modalVisible.value = false;
  resetForm();
  finished.value = false;
  isSending.value = false;
}
</script>

<template>
  <div>
    <button
      class="ce-regulatoryDocumentRequestCta ce-btn ce-btn-light ce-btn--tiny"
      @click.prevent="showModal">
      {{ $t('regulatoryDocumentRequest.cta') }}
    </button>

    <Teleport to="body">
      <modal
        v-if="modalVisible"
        class="ce-regulatoryDocumentRequestModal"
        :headline="$t('regulatoryDocumentRequest.modal.headline')"
        @close="hideModal">
        <div
          class="ce-modalContent"
          v-if="!finished">
          <alert
            class="mb3"
            v-if="hasMessage"
            :message="message.content"
            :type="message.type"
            @close="message = {}" />

          <div class="ce-field">
            <label>Product</label>

            <div
              class="productInfo">
              <div
                v-if="colorImage"
                class="colorBadgeHolder"
                :style="{'background-image': `url(${colorImage})`}" />

              <div>
                {{ pigment.product_name }}
              </div>
            </div>
          </div>

          <form
            @submit="submit"
            class="form mt4">
            <validated-select
              name="documentType"
              :label="$t('regulatoryDocumentRequest.modal.documentType')"
              :options="documentTypeOptions"
              rules="required" />
          </form>

          <div
            v-if="consentHintNecessary"
            class="ms-1 line-height-4">
            <i18n-t
              keypath="contactForm.consentHint"
              tag="p"
              scope="global">
              <a
                :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
                rel="noopener noreferrer">
                {{ $t('contactForm.dataProtectionPage') }}</a>
            </i18n-t>
          </div>

          <div class="mt4">
            <button
              class="ce-btn ce-btn-light ce-btn--fullWidth"
              @click.prevent="submit">
              <template v-if="isSending">
                <fa-icon
                  icon="spinner"
                  spin />
              </template>
              <template v-else>
                {{ $t('regulatoryDocumentRequest.modal.submitCta') }}
              </template>
            </button>
          </div>
        </div>

        <div
          class="ce-modalContent"
          v-else>
          <p>{{ $t('regulatoryDocumentRequest.finished.message') }}</p>

          <div class="mt4">
            <button
              class="ce-btn ce-btn-light ce-btn--fullWidth"
              @click.prevent="hideModal">
              {{ $t('regulatoryDocumentRequest.finished.cta') }}
            </button>
          </div>
        </div>
      </modal>
    </Teleport>
  </div>
</template>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-btn.ce-regulatoryDocumentRequestCta {
  white-space: normal;
}

.ce-regulatoryDocumentRequestModal {
  .ce-modalInner {
    overflow: visible;
  }

  .productInfo {
    display: flex;
    align-items: center;

    .colorBadgeHolder {
      width: 70px;
      height: 40px;
      border-radius: @border-radius-lg;
      overflow: hidden;
      margin-right: @space-2;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: cover;
      max-width: 100%;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }
}
</style>
